import React from "react";
import { Grid, TextField, MenuItem, Button } from "@material-ui/core";
import clsx from "clsx";
import { useStyles } from "../../../../shared/styles/useStyles";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducers/rootReducer";
import { MyAlert } from "../../../../components/custom/MyAlert";
import { SpecialDay } from "../../../../module/superAdmin/form/SpecialDay";
import { startAddSpecialDay } from "../../../../redux/actions/entitiesActions";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  uiCloseErrorAlert,
  uiCloseSuccessAlert,
  setSuccessMsg,
  setErrorMsg,
  uiOpenErrorAlert,
} from "../../../../redux/actions/uiActions";
import { useIntl, FormattedMessage } from "react-intl";

interface Props {
  selectedDate: string;
  selectedType: string;
  setSelectedDate: (val: string) => void;
  setSelectedType: (val: string) => void;
  specialDays: SpecialDay[];
  setSpecialDays: React.Dispatch<React.SetStateAction<SpecialDay[]>>;
}

const dayTypeMap: { [es: string]: string } = {
  "Día festivo nacional": "NationalHoliday",
  "Día cívico": "CivicDay",
  "Día religioso": "ReligiousDay",
  "Día compensatorio": "CompensatoryDay",
  "Día institucional": "InstitutionalDay",
  "Día especial local": "LocalSpecialDay",
  "Día de calamidad/emergencia": "EmergencyDay",
  "Día electoral": "ElectionDay",
};

export const SpecialDaysForm: React.FC<Props> = ({
                                                   selectedDate,
                                                   selectedType,
                                                   setSelectedDate,
                                                   setSelectedType,
                                                   specialDays,
                                                   setSpecialDays,
                                                 }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { successAlert, successMsg, errorAlert, errorMsg } = useSelector(
    (state: AppState) => state.ui
  );

  const initialValues = {
    date: selectedDate,
    type: selectedType,
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(intl.formatMessage({ id: "RequiredDate" })),
    type: Yup.string().required(intl.formatMessage({ id: "RequiredDayType" })),
  });

  const handleSubmit = async (values: typeof initialValues, { resetForm }: any) => {
    const exists = specialDays.some(
      (d) => d.date === values.date && d.type === values.type
    );

    if (exists) {
      dispatch(setErrorMsg(intl.formatMessage({ id: "DuplicateSpecialDay" })));
      dispatch(uiOpenErrorAlert());
      return;
    }

    const newDay: SpecialDay = {
      id: Date.now().toString(),
      date: values.date,
      type: values.type,
    };

    await dispatch<any>(startAddSpecialDay(newDay));
    dispatch(setSuccessMsg(intl.formatMessage({ id: "SpecialDaySaved" })));

    setSpecialDays([...specialDays, newDay]);
    setSelectedDate("");
    setSelectedType("");
    resetForm();
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, handleChange, values }) => (
          <Form>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="date"
                  label={intl.formatMessage({ id: "Date" })}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  name="date"
                  value={values.date}
                  onChange={(e) => {
                    handleChange(e);
                    setSelectedDate(e.target.value);
                  }}
                  error={touched.date && Boolean(errors.date)}
                  helperText={touched.date && errors.date}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  select
                  label={intl.formatMessage({ id: "DayType" })}
                  variant="outlined"
                  size="small"
                  name="type"
                  value={values.type}
                  onChange={(e) => {
                    handleChange(e);
                    setSelectedType(e.target.value);
                  }}
                  error={touched.type && Boolean(errors.type)}
                  helperText={touched.type && errors.type}
                >
                  {Object.entries(dayTypeMap).map(([es, en]) => (
                    <MenuItem key={en} value={en}>
                      {es}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <Button
                  className={clsx(classes.btn, classes.save)}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  <FormattedMessage id="Add" />
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>

      <MyAlert
        open={successAlert}
        typeAlert="success"
        message={successMsg ?? ""}
        time={2000}
        handleClose={() => dispatch(uiCloseSuccessAlert())}
      />

      <MyAlert
        open={errorAlert}
        typeAlert="error"
        message={errorMsg ?? ""}
        time={3000}
        handleClose={() => dispatch(uiCloseErrorAlert())}
      />
    </>
  );
};
